/**
 * JWT Token refresh utilities
 */
// IMPORTANT: call setupAutoRefresh() after login (or when auth state is established).

import { refreshTokenWithLambda } from '../repository/authRepository';

interface TokenResponse {
  access: string;
  refresh?: string; // Only returned when refresh tokens are rotated
}

/**
 * Refresh the access token using the refresh token
 */
export async function refreshAccessToken(): Promise<string | null> {
  try {
    const refreshToken = localStorage.getItem('refresh_token');
    if (!refreshToken) {
      console.error('[refreshAccessToken] No refresh token found');
      return null;
    }

    console.log('[refreshAccessToken] Refreshing access token...');

    const result = await refreshTokenWithLambda(refreshToken);

    if ('error' in result) {
      console.error('[refreshAccessToken] Token refresh failed:', result.error);
      // Clear invalid tokens
      localStorage.removeItem('access_token');
      localStorage.removeItem('refresh_token');
      return null;
    }

    // Store the new access token
    localStorage.setItem('access_token', result.access);
    // Only update refresh token if a new one was provided
    if (result.refresh) {
      localStorage.setItem('refresh_token', result.refresh);
    }

    console.log('[refreshAccessToken] Token refresh successful');
    return result.access;

  } catch (error) {
    console.error('[refreshAccessToken] Error refreshing token:', error);
    // Clear invalid tokens
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    return null;
  }
}

/**
 * Checks if the access token is expired or about to expire
 * Returns true if token needs refresh
 */
export function isTokenExpired(bufferSeconds: number = 30): boolean {
  const accessToken = localStorage.getItem('access_token');
  if (!accessToken) return true;

  try {
    // Decode the JWT payload (base64)
    const payload = JSON.parse(atob(accessToken.split('.')[1]));
    const exp = payload.exp;
    const now = Math.floor(Date.now() / 1000);

    // Check if token expires within buffer time
    return exp < (now + bufferSeconds);
  } catch (error) {
    console.error('Error decoding token:', error);
    return true;
  }
}

/**
 * Fetch with authentication for protected endpoints
 */
export async function fetchWithAuth(url: string, options: RequestInit = {}): Promise<Response> {
  console.log(`[fetchWithAuth] Starting authenticated request to ${url}`);

  try {
    // Check token expiry
    const accessToken = localStorage.getItem('access_token');
    const tokenExpired = isTokenExpired();

    if (tokenExpired) {
      console.log(`[fetchWithAuth] Token expired, refreshing...`);
      const newToken = await refreshAccessToken();
      if (!newToken) {
        console.error(`[fetchWithAuth] Token refresh failed`);
        // Don't throw immediately - let the request proceed with the existing token
        // The server will return 401 if it's truly expired and we'll retry then
      } else {
        console.log(`[fetchWithAuth] Token refreshed successfully`);
      }
    }

    const finalAccessToken = localStorage.getItem('access_token');

    const headers: Record<string, string> = {
      'Accept': 'application/json',
      'Authorization': `Bearer ${finalAccessToken}`,
      ...(options.headers as Record<string, string> || {}),
    };

    // If the body is *not* FormData, default to JSON content-type
    if (!(options.body instanceof FormData)) {
      headers['Content-Type'] = 'application/json';
    }

    const authOptions = { ...options, headers };

    let response = await fetch(url, authOptions);
    console.log(`[fetchWithAuth] First fetch completed, status: ${response.status}`);

    if (response.status === 401) {
      console.log(`[fetchWithAuth] Got 401, attempting token refresh...`);
      const newToken = await refreshAccessToken();
      if (newToken) {
        console.log(`[fetchWithAuth] Token refreshed, retrying request...`);
        headers['Authorization'] = `Bearer ${newToken}`;
        response = await fetch(url, { ...options, headers });
        console.log(`[fetchWithAuth] Retry fetch completed, status: ${response.status}`);
      } else {
        console.error(`[fetchWithAuth] Token refresh failed on 401`);
      }
    }

    return response;
  } catch (error) {
    console.error(`[fetchWithAuth] Request failed:`, error);
    throw error;
  }
}


/**
 * Sets up automatic token refresh before expiry
 */
let refreshInterval: ReturnType<typeof setInterval> | null = null;

export function setupAutoRefresh(): void {
  // Clear any existing interval
  if (refreshInterval) {
    clearInterval(refreshInterval);
  }

  // Check every hour if token needs refresh (tokens last 7 days, so hourly checks are fine)
  refreshInterval = setInterval(async () => {
    if (isTokenExpired(3600)) { // Refresh if expires within 1 hour
      console.log('Auto-refreshing JWT token...');
      await refreshAccessToken();
    }
  }, 3600000); // Check every hour

  // Also refresh immediately if needed
  if (isTokenExpired(3600)) {
    refreshAccessToken();
  }
}

/**
 * Clears the auto-refresh interval (useful for cleanup)
 */
export function clearAutoRefresh(): void {
  if (refreshInterval) {
    clearInterval(refreshInterval);
    refreshInterval = null;
  }
}
